import type { TurnBlock } from '~/deck15/chat/turnModel';
import { toolFromLabel } from '~/deck15/chat/wireVocabulary';
import type { ContextRequestEvent, MessagePart, PlanningChatMessage } from '~/types/planning';

/**
 * The hero's conversations, written as the server stores them, and the turn that writes the deck,
 * played the way the agent really plays it.
 *
 * Every earlier turn is a real `PlanningChatMessage` with real `parts`, so the workspace's own
 * `DeckThread` rebuilds it through `blocksFromMessage` exactly as it rebuilds a conversation after a
 * reload. The writing turn follows the storytelling agent's loop: one short line and one tool call per
 * step, the brand kit read before an artifact when a kit is bound, `create_artifact` one artifact at a
 * time, and the stranger read at the end.
 */

const part = (type: MessagePart['type'], content: string): MessagePart => ({ type, content, specialist: null });

/** What an assistant turn did, in order. A question also carries the request it answered. */
export interface DemoStep {
  part: MessagePart;
  request?: ContextRequestEvent;
}

export const thought = (text: string, durationSec: number): DemoStep => ({
  part: part('THINKING_CHECKPOINT', JSON.stringify({ text, durationSec })),
});

export const say = (text: string): DemoStep => ({ part: part('TEXT', text) });

/** A finished tool row, persisted as its final wording (the backend's `ToolStepLabels` done label). */
export const step = (label: string): DemoStep => ({ part: part('ACTION', label) });

export const webSearch = (query: string, sources: string[]): DemoStep => ({
  part: part('WEB_SEARCH_CALL', JSON.stringify({ query, results: sources.map((title) => ({ title })) })),
});

export const linearIssues = (issues: [identifier: string, title: string][]): DemoStep => ({
  part: part('LINEAR_READ', JSON.stringify({ mode: 'read', issues: issues.map(([identifier, title]) => ({ identifier, title })) })),
});

export const pullRequests = (repo: string, prs: [number: string, title: string][]): DemoStep => ({
  part: part('GITHUB_READ', JSON.stringify({ mode: 'prs', repo, items: prs.map(([number, title]) => ({ number, title })) })),
});

/** The outline as chapters of artifact names, every one written. */
export const outline = (chapters: [name: string, canvases: string[]][]): DemoStep => ({
  part: part(
    'OUTLINE_UPDATE',
    JSON.stringify({
      chapters: chapters.map(([name, canvases]) => ({
        name,
        canvases: canvases.map((title) => ({ title, status: 'complete' })),
      })),
    }),
  ),
});

let asked = 0;

/** The agent asked, and the user picked `answer` from `options`. */
export const question = (text: string, options: string[], answer: string): DemoStep => {
  asked += 1;
  const requestId = `demo-question-${asked}`;
  return {
    part: part('CONTEXT_COMPLETED', JSON.stringify({ type: 'CLARIFICATION', requestId })),
    request: {
      type: 'CLARIFICATION',
      requestId,
      messageId: '',
      status: 'COMPLETED',
      clarificationData: {
        items: [{ question: text, type: 'single', options: options.map((label) => ({ label })), answer, skipped: false }],
      },
    },
  };
};

/** One exchange that already happened. */
export interface DemoTurn {
  user: string;
  files?: string[];
  /** Minutes before the demo opens that the user sent it. Over a day puts it under "Yesterday". */
  minutesAgo: number;
  agent: DemoStep[];
}

/** One step of the writing turn: a line said, then one tool (by its finished label). */
export interface DemoBeat {
  say?: string;
  step?: string;
}

/** The turn the demo opens on: the user's message and the agent's loop that writes the deck. */
export interface DemoLiveTurn {
  user: string;
  files?: string[];
  /** The reasoning it settled before its first step. */
  thought: string;
  thoughtSec: number;
  /** Steps before the first artifact. */
  opening: DemoBeat[];
  /** The line the agent says before each artifact, in the deck's order. */
  lines: string[];
  /** A kit is bound: the agent reads the kit's composition for each artifact before writing it. */
  readsKit?: boolean;
  /** Steps after the last artifact. */
  closing: DemoBeat[];
  /** The last thing it says: the turn's summary, in the answer's markdown. */
  outro: string;
}

/** The backend's running wording for each finished label the demo uses (`ToolStepLabels`). */
const RUNNING: Record<string, string> = {
  'Created an artifact': 'Creating an artifact',
  'Read the brand kit': 'Reading the brand kit',
  'Read the point card': 'Reading the point card',
  'Saved the point card': 'Saving the point card',
  'Read the deck as a stranger': 'Reading the deck as a stranger',
  'Read Figma frames': 'Reading Figma frames',
  'Reviewed your images': 'Reviewing your images',
  'Read your files': 'Reading your files',
};

/* ── the clock ─────────────────────────────────────────────────────────────────────────────── */

/** A line lands, then the model writes its tool call (the thread's Thinking mark holds the gap). */
const SAY_MS = 700;
const TOOL_CALL_MS = 500;
/** A plain tool runs this long between its start and result rows. */
const TOOL_MS = 550;
/** The model writing an artifact's whole document into `create_artifact`, before the tool starts. */
const WRITE_MS = 1100;
/** The document replaying onto the workspace, phase by phase, before it commits. */
const REPLAY_MS = 1300;
const AFTER_MS = 250;
/** `useGenMorph`'s resolve: the blur landing on the finished artifact. */
const RESOLVE_MS = 2550;
/**
 * How long a finished artifact stays on screen before the preview follows the agent to the next one.
 * The agent is already writing the next document meanwhile, so its line and tool call still land in the
 * thread; only the NEXT artifact's start waits, so each result is actually seen.
 */
const SHOW_MS = 1400;

/** The phases `describeArtifactProgress` reads off a document as it arrives, in the order they appear. */
export const REPLAY_PHASES = [
  'Declaring the artifact',
  'Choosing the typeface',
  'Setting the palette',
  'Writing the styles',
  'Laying out the slide',
];

export type Cue =
  | { at: number; kind: 'say'; text: string }
  | { at: number; kind: 'tool'; label: string; doneAt: number }
  | { at: number; kind: 'artifact'; index: number; doneAt: number };

export interface Timeline {
  cues: Cue[];
  /** When the turn ends and settles into its recap. */
  endAt: number;
}

export function timelineOf(live: DemoLiveTurn): Timeline {
  const cues: Cue[] = [];
  let t = 400;
  const beat = (b: DemoBeat) => {
    if (b.say) {
      cues.push({ at: t, kind: 'say', text: b.say });
      t += SAY_MS;
    }
    if (b.step) {
      t += TOOL_CALL_MS;
      cues.push({ at: t, kind: 'tool', label: b.step, doneAt: t + TOOL_MS });
      t += TOOL_MS + AFTER_MS;
    }
  };
  live.opening.forEach(beat);
  let shownAt = 0;
  live.lines.forEach((line, index) => {
    if (live.readsKit) beat({ step: 'Read the brand kit' });
    cues.push({ at: t, kind: 'say', text: line });
    t = Math.max(t + WRITE_MS, shownAt);
    cues.push({ at: t, kind: 'artifact', index, doneAt: t + REPLAY_MS });
    shownAt = t + REPLAY_MS + RESOLVE_MS + SHOW_MS;
    t += REPLAY_MS + AFTER_MS;
  });
  t = Math.max(t, shownAt - SHOW_MS);
  live.closing.forEach(beat);
  cues.push({ at: t, kind: 'say', text: live.outro });
  return { cues, endAt: t + 900 };
}

/** The live turn's blocks at `t` ms into the turn. */
export function liveBlocksAt(live: DemoLiveTurn, timeline: Timeline, t: number): TurnBlock[] {
  const blocks: TurnBlock[] = [
    { kind: 'thinking', id: 'live-thought', text: live.thought, durationSec: live.thoughtSec, live: false },
  ];
  const visible = timeline.cues.filter((cue) => cue.at <= t);
  visible.forEach((cue, i) => {
    const sealed = i < visible.length - 1;
    if (cue.kind === 'say') {
      blocks.push({ kind: 'prose', id: `live-say-${i}`, text: cue.text, open: !sealed });
      return;
    }
    const doneLabel = cue.kind === 'artifact' ? 'Created an artifact' : cue.label;
    const done = t >= cue.doneAt;
    blocks.push({
      kind: 'activity',
      id: `live-tool-${i}`,
      tool: toolFromLabel(doneLabel),
      label: done ? doneLabel : RUNNING[doneLabel] ?? doneLabel,
      lifecycle: done ? 'complete' : 'running',
      body: { kind: 'none' },
    });
  });
  return blocks;
}

/** The finished record of the writing turn, as the server keeps it. */
function liveRecord(live: DemoLiveTurn, timeline: Timeline): DemoStep[] {
  return [
    thought(live.thought, live.thoughtSec),
    ...timeline.cues.map((cue) =>
      cue.kind === 'say' ? say(cue.text) : step(cue.kind === 'artifact' ? 'Created an artifact' : cue.label),
    ),
  ];
}

/* ── the thread ────────────────────────────────────────────────────────────────────────────── */

const file = (filename: string) => ({ filename, url: '', mimeType: '', sizeBytes: 0 });

function userMessage(id: string, text: string, at: Date, files?: string[]): PlanningChatMessage {
  return { id, role: 'user', content: text, createdAt: at, actions: [], parts: null, pendingContextRequests: null, files: files?.map(file), isComplete: true };
}

function agentMessage(id: string, at: Date, steps: DemoStep[]): PlanningChatMessage {
  const requests = steps.flatMap((s) => (s.request ? [{ ...s.request, messageId: id }] : []));
  return {
    id,
    role: 'assistant',
    content: steps.filter((s) => s.part.type === 'TEXT').map((s) => s.part.content).join('\n\n'),
    createdAt: at,
    actions: [],
    parts: steps.map((s) => s.part),
    pendingContextRequests: requests.length ? requests : null,
    isComplete: true,
  };
}

export const LIVE_ID = 'demo-live';

/**
 * The thread at a moment of the demo. While the turn runs, its assistant message is empty and its
 * blocks come from `liveBlocksAt`; once `done`, it is stored like the others and folds into its recap.
 *
 * No artifact receipts: those belong to browser-run generation, and a storytelling turn writes through
 * `create_artifact`, so its recap reads "Worked" with no duration, exactly as the workspace shows it.
 */
export function buildThread(
  history: DemoTurn[],
  live: DemoLiveTurn,
  timeline: Timeline,
  opts: { openedAt: number; done: boolean },
): PlanningChatMessage[] {
  const messages: PlanningChatMessage[] = [];

  history.forEach((turn, i) => {
    const at = opts.openedAt - turn.minutesAgo * 60_000;
    const agentId = `demo-agent-${i}`;
    messages.push(userMessage(`demo-user-${i}`, turn.user, new Date(at), turn.files));
    messages.push(agentMessage(agentId, new Date(at + 1000), turn.agent));
  });

  const liveAt = opts.openedAt - 2000;
  messages.push(userMessage('demo-user-live', live.user, new Date(liveAt), live.files));
  messages.push(agentMessage(LIVE_ID, new Date(liveAt + 1000), opts.done ? liveRecord(live, timeline) : []));
  return messages;
}
